import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import Vue from 'vue';
import { generateDraftId } from '@/composables/useStructuredDataDraft';
import { hasPendingImportRows } from '@/utils/sde-bulk-utils';
import { buildMetadataFieldInfoMap } from '@/utils/sde-row-utils';
import {
    csvMatrixToSdeRowsAsync,
} from '@/utils/structured-data-csv-bridge';
import { filterSdeConfigToMappedColumns } from '@/utils/sde-config-builder';
import structuredDataEditor from './structured-data-editor';

function convertPayload(fields, data, fields_mapping) {
    var payload = [];
    data.forEach((item) => {
        var mapped_item = {};
        var index = 0;

        item.forEach((col) => {
            if (col == "") {
                col = null;
            }

            if (fields_mapping[index]) {
                let field = fields.find((item) => item && item.value == fields_mapping[index]);
                if (field) {
                    let dict = mapped_item;
                    let parts = fields_mapping[index].split(".");
                    let last_field = parts[parts.length - 1];
                    parts = parts.slice(0, parts.length - 1);
                    parts.forEach((fieldPart) => {
                        if (!dict[fieldPart]) {
                            dict[fieldPart] = {};
                        }
                        dict = dict[fieldPart];
                    });
                    if (field.type == "number") {
                        col = Number.parseFloat(col);
                    } else if (field.type == "boolean") {
                        if (col !== null) {
                            const colLower = col.toLowerCase();
                            if (colLower == "false" ||
                                colLower == "no" ||
                                colLower == "0" ||
                                colLower == Vue.prototype.$translate("false") ||
                                colLower == Vue.prototype.$translate("no")
                            ) {
                                col = false;
                            } else {
                                col = (
                                    colLower == "1" ||
                                    colLower == "true" ||
                                    colLower == "yes" ||
                                    colLower == Vue.prototype.$translate("true") ||
                                    colLower == Vue.prototype.$translate("yes")
                                );
                            }
                        }
                    } else if (field.type == "phone number" || field.type == "phone_number" ||
                               field.value == "phone_number" || field.value == "mobile_phone_number" ||
                               last_field == "phone_number" || last_field == "mobile_phone_number") {
                        if (col !== null && typeof col === "string") {
                            col = col.replace(/\s+/g, "");
                        }
                    }

                    dict[last_field] = col;
                }
            }
            index += 1;
        });
        payload.push(mapped_item);
    });
    return payload;
}

function syncErrorApplyOptions(state, rootState) {
    return {
        metadataFieldInfoMap: buildMetadataFieldInfoMap(rootState.producttypes?.producttypes),
        columns: state.structuredDataEditor?.config?.columns || [],
    };
}

function mapErrors(payload, error) {
    if (!error?.payload) {
        return null;
    }
    error.payload.forEach((item, index) => {
        if (item.error) {
            payload[index] = {
                errors: { non_field_errors: [item.error] },
                item: payload[index] || {},
            };
        } else if (item.errors) {
            payload[index] = {
                errors: item.errors,
                item: payload[index] || item.item || {},
            };
        } else {
            payload[index] = {
                errors: item,
                item: payload[index] || {},
            };
        }
    });
    return payload;
}

/**
 * Build API destock payload from SDE rows.
 * Quantities must be expressed in each product's stock unit (pc, kg, L, etc.).
 *
 * @param {object[]} payload
 * @returns {Array<{ upc: string, quantity: number }|null>}
 */
function buildDestockPayload(payload) {
    return payload
        .map((item) => {
            const upc = item.upc;
            const quantity = item.quantity;

            if (!upc || (typeof upc === 'string' && !upc.trim())) {
                return null;
            }

            return {
                upc: typeof upc === 'string' ? upc.trim() : upc,
                quantity,
            };
        })
        .filter((item) => item !== null && item.quantity && item.quantity > 0);
}

function createState() {
    return {
        file: null,
        data: null,
        step: 'selectfile',
        error: false,
        server_error: false,
        global_errors: null,
        payload: null,
        fields: [],
        fields_mapping: [],
        ignore_first_line: false,
        extra_params: {},
    };
}

const mutations = {
    updateStep(state, step) {
        state.step = step;
    },
    updateError(state, error) {
        state.error = error;
    },
    updateServerError(state, server_error) {
        state.server_error = server_error;
    },
    updateFile(state, file) {
        state.file = file;
    },
    updateData(state, data) {
        state.data = data;
    },
    updatePayload(state, payload) {
        state.payload = payload;
    },
    updateFieldsMapping(state, fields_mapping) {
        state.fields_mapping = fields_mapping;
    },
    updateFieldMapping(state, params) {
        state.fields_mapping.forEach((item, item_index) => {
            if (item_index != params.index) {
                if (params.value == item) {
                    state.fields_mapping[item_index] = null;
                }
            } else {
                state.fields_mapping[item_index] = params.value;
            }
        });
    },
    updateFields(state, fields) {
        state.fields = fields;
    },
    updateIgnoreFirstLine(state, ignore_first_line) {
        state.ignore_first_line = ignore_first_line;
    },
    updateGlobalErrors(state, global_errors) {
        state.global_errors = global_errors;
    },
    updateExtraParams(state, extra_params) {
        state.extra_params = extra_params;
    },
};

const actions = {
    validateFieldsMapping(state) {
        const missing_fields = [];
        state.fields.forEach((field) => {
            if (field.mandatory && !state.fields_mapping.includes(field.value)) {
                missing_fields.push(field.text);
            }
        });
        if (missing_fields.length) {
            throw {
                code: 400,
                success: false,
                details: Vue.prototype.$translate("theFollowingMandatoryFieldsHaveNotBeenMapped") + " " + missing_fields.join(", "),
            };
        }
    },

    changeData({ commit, dispatch, state }, params) {
        commit('updateData', params.data);
        commit('updateStep', 'configure');
        dispatch("guessFieldsMapping");
    },

    async prepareEditorFromCsv({ commit, state }, { sdeConfig }) {
        actions.validateFieldsMapping(state);
        const data = state.ignore_first_line ? state.data.slice(1) : state.data;
        const editorConfig = filterSdeConfigToMappedColumns(
            sdeConfig,
            state.fields_mapping
        );

        commit('structuredDataEditor/setConfig', editorConfig);
        commit('structuredDataEditor/setSessionId', null);
        commit('structuredDataEditor/setDraftId', generateDraftId());
        commit('structuredDataEditor/setDirty', false);
        commit('structuredDataEditor/resetNewRowsCounter');

        const rows = await csvMatrixToSdeRowsAsync(
            data,
            state.fields_mapping,
            state.fields,
        );
        commit('structuredDataEditor/setRows', rows);
        commit('updateStep', 'edit');
    },

    async synchronize({ commit, dispatch, state, rootState }, params) {
        const fromEditor = Boolean(params?.useExistingPayload);
        const parentState = rootState.synchronizedestock;

        if (!parentState.stock) {
            throw {
                code: 400,
                success: false,
                details: Vue.prototype.$translate("stockRequired"),
            };
        }

        if (!fromEditor) {
            actions.validateFieldsMapping(state);
        }
        // Also when importing from the editor: the stepper shows the loader
        // on the import step, and moves back to "edit" on error or partial.
        commit('updateStep', 'synchronizing');

        const payload = fromEditor
            ? state.payload
            : convertPayload(
                state.fields,
                state.ignore_first_line ? state.data.slice(1) : state.data,
                state.fields_mapping
            );
        commit('updatePayload', payload);

        const destockPayload = buildDestockPayload(payload);
        if (destockPayload.length === 0) {
            throw {
                code: 400,
                success: false,
                details: Vue.prototype.$translate("noValidItemsToDestock"),
            };
        }

        try {
            const response = await axios.post(
                `/api/stocks/${parentState.stock}/destock/`,
                {
                    payload: destockPayload,
                    destock_components_if_needed: parentState.destock_components_if_needed,
                }
            );

            const mappedPayload = response.data.payload.map((result, index) => {
                if (result.errors && Object.keys(result.errors).length > 0) {
                    return {
                        errors: result.errors,
                        item: payload[index] || {},
                    };
                }
                return {
                    item: payload[index] || {},
                };
            });

            commit('updatePayload', mappedPayload);
            commit('updateError', false);
            commit('updateGlobalErrors', null);
            dispatch('session/fetchStats', null, { root: true });

            if (fromEditor) {
                commit('structuredDataEditor/markRowsFromSyncPayload', {
                    syncPayload: mappedPayload,
                    ...syncErrorApplyOptions(state, rootState),
                });
                if (hasPendingImportRows(state.structuredDataEditor.rows)) {
                    commit('updateStep', 'edit');
                    return { partial: true, payload: mappedPayload };
                }
            }

            commit('updateStep', 'results');
            return { partial: false, payload: mappedPayload };
        } catch (xhr_error) {
            commit('updateError', true);

            let error = utils.handleError(xhr_error);
            commit('updateServerError', Number.parseInt(error.code / 100) == 5);

            if (Number.parseInt(error.code / 100) == 4) {
                if (error.details?.payload) {
                    const mappedPayload = error.details.payload.map((result, index) => {
                        if (result && Object.keys(result).length > 0) {
                            return {
                                errors: result,
                                item: payload[index] || {},
                            };
                        }
                        return {
                            item: payload[index] || {},
                        };
                    });
                    commit('updatePayload', mappedPayload);
                    if (fromEditor) {
                        commit('structuredDataEditor/markRowsFromSyncPayload', {
                            syncPayload: mappedPayload,
                            persistSuccess: false,
                            ...syncErrorApplyOptions(state, rootState),
                        });
                        commit('structuredDataEditor/setDirty', true);
                    }
                } else {
                    let payload_errors = mapErrors(payload, error.details);
                    if (payload_errors) {
                        commit('updatePayload', payload_errors);
                        if (fromEditor) {
                            commit('structuredDataEditor/markRowsFromSyncPayload', {
                                syncPayload: payload_errors,
                                persistSuccess: false,
                                ...syncErrorApplyOptions(state, rootState),
                            });
                            commit('structuredDataEditor/setDirty', true);
                        }
                    } else if (error.details.detail) {
                        commit('updateGlobalErrors', [error.details.detail]);
                    } else if (error.details.non_field_errors) {
                        commit('updateGlobalErrors', error.details.non_field_errors);
                    } else {
                        commit('updateGlobalErrors', [Vue.prototype.$translate("anUnexpectedErrorHappenedPleaseContactUsForSupport")]);
                    }
                }
            } else {
                commit('updateGlobalErrors', []);
            }

            commit('updateStep', fromEditor ? 'edit' : 'results');
            throw error;
        }
    },

    async init({ commit }, params) {
        await commit('updateStep', 'selectfile');
        await commit('updateFile', null);
        await commit('updateData', null);
        await commit('updatePayload', null);
        await commit('updateFields', null);
        await commit('updateIgnoreFirstLine', false);
        await commit('updateGlobalErrors', null);
        await commit('updateExtraParams', params?.extra_params || {});
    },

    guessFieldsMapping({ commit, state }) {
        const first_line = state.data[0];
        let mapping_found = false;
        let fields_mapping = [];
        first_line.forEach((cell) => {
            const field = state.fields.find((field) => field.value == cell || field.text == cell || (field.header && field.header == cell));
            fields_mapping.push(field ? field.value : null);
            if (field) {
                mapping_found = true;
            }
        });
        if (mapping_found) {
            commit('updateFieldsMapping', fields_mapping);
            commit('updateIgnoreFirstLine', mapping_found);
        }
    },
};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions,
    modules: {
        structuredDataEditor: structuredDataEditor(),
    },
});
